/**
 * What tells a technician the code was read without looking: a short buzz and
 * a short tone. Request of 4 October 2026 (second), section 5.
 *
 * iPhone has no vibration for the web and plays no sound until the page has
 * been touched, so the tone's audio context is created and woken on the first
 * touch of the scanner, and the buzz is simply skipped where there is none.
 */

import { haptic } from '@/ui/motion/haptics';

let audio: AudioContext | null = null;

/** Wakes the audio on a touch, so the tone may play later without one. */
export function unlockSound(): void {
  try {
    const Context =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Context === undefined) return;
    audio ??= new Context();
    if (audio.state === 'suspended') void audio.resume();
  } catch {
    audio = null;
  }
}

export function signalScan(): void {
  haptic('success');
  try {
    if (audio === null || audio.state !== 'running') return;
    const tone = audio.createOscillator();
    const gain = audio.createGain();
    tone.type = 'sine';
    tone.frequency.value = 1320;
    gain.gain.setValueAtTime(0.18, audio.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + 0.09);
    tone.connect(gain).connect(audio.destination);
    tone.start();
    tone.stop(audio.currentTime + 0.1);
  } catch {
    // A tone is a nicety; a scan never fails for want of one.
  }
}

export type Platform = 'iphone' | 'iphoneInstalled' | 'android' | 'other';

/** Which permission steps to show. */
export function platform(): Platform {
  const agent = navigator.userAgent;
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches);
  if (/iPhone|iPad|iPod/.test(agent)) return standalone ? 'iphoneInstalled' : 'iphone';
  if (/Android/.test(agent)) return 'android';
  return 'other';
}
